import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo, type ReactNode } from 'react'
import {
  Box,
  Table,
  Tbody,
  Tr,
  Td,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TableHeader, type TableHeaderRef } from './TableHeader'
import { TableBody, type TableBodyRef } from './TableBody'
import { Pagination } from './Pagination'
import type { DataTableOptions, Permission, SortParams, MetaData } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'

interface DataTableProps {
  options: DataTableOptions
  params?: Record<string, any>
  onOpenModal?: (id: any) => void
  setSubSub?: (all: number, trashed: number) => void
  access?: Permission
  filterOptions?: Record<string, Array<{ value: string | number; label: string }>>
  customRenderers?: Record<string, (value: any, row?: any) => ReactNode>
}

export interface DataTableRef {
  getSelectedRows: () => number[]
  clearPage: () => void
  reload: () => void
  applyFilter: (field: string, value: any) => void
}

export const DataTable = forwardRef<DataTableRef, DataTableProps>(
  ({ options, params, onOpenModal, setSubSub, access, filterOptions, customRenderers }, ref) => {
    const { showError, showSuccess } = useToastNotification()
    const [sortParams, setSortParams] = useState<SortParams>({ order: '', sort: 'asc' })
    const [isLoading, setIsLoading] = useState(false)
    const [dataRows, setDataRows] = useState<any[]>([])
    const [totalRows, setTotalRows] = useState<number | null>(null)
    const [metaData, setMetaData] = useState<MetaData | null>(null)
    const [nextPage, setNextPage] = useState<string | null>(null)
    const [toDelete, setToDelete] = useState<any>(null)
    const [toRestore, setToRestore] = useState<any>(null)
    const [deleteAction, setDeleteAction] = useState<string>('') // Track if it's 'Trash' or 'Delete'
    const [filters, setFilters] = useState<Record<string, any>>({})

    const theadRef = useRef<TableHeaderRef>(null)
    const tbodyRef = useRef<TableBodyRef>(null)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [modalDescription, setModalDescription] = useState('')

    const colors = useThemeColors()
    const spinnerColor = 'blue.500'
    const loadingTextColor = colors.text
    const emptyTextColor = colors.textSecondary
    const tableBg = colors.cardBg
    const borderColor = colors.border
    const scrollbarTrack = colors.scrollbarTrack
    const scrollbarThumbHover = colors.scrollbarThumbHover
    const theadBg = colors.cardBg

    const mergedDataFields = useMemo(() => {
      if (!filterOptions) return options.dataFields

      const merged = { ...options.dataFields }
      Object.keys(filterOptions).forEach((field) => {
        if (merged[field]) {
          merged[field] = {
            ...merged[field],
            withFilter: true,
            filterOptions: filterOptions[field],
          }
        }
      })
      return merged
    }, [options.dataFields, filterOptions])

    const handleSort = (field: string, sort: 'asc' | 'desc') => {
      setSortParams({
        order: field,
        sort,
      })
    }

    const handleFilter = (field: string, value: any) => {
      setFilters((prev) => {
        if (value === null || value === undefined || value === '') {
          const newFilters = { ...prev }
          delete newFilters[field]
          return newFilters
        }
        return {
          ...prev,
          [field]: value,
        }
      })
      setNextPage(null)
    }

    const handleAction = (id: any, action: string) => {
      setToRestore(null)
      setToDelete(null)
      setDeleteAction('')

      if (action === 'Edit' && options?.displayInModal) {
        onOpenModal?.(id)
        return
      }

      const actionMessages: Record<string, string> = {
        Restore: 'Do you really want to restore this record?',
        Trash: 'Do you really want to delete this record?',
        Delete: 'You are about to permanently delete these items from your site. This action cannot be undone.',
        BulkDelete: 'You are about to permanently delete multiple items from your site. This action cannot be undone.',
        BulkRestore: 'Do you really want to restore multiple records?',
      }

      if (actionMessages[action]) {
        setToRestore(action === 'Restore' || action === 'BulkRestore' ? id : null)
        if (action === 'Trash' || action === 'Delete' || action === 'BulkDelete') {
          setToDelete(id)
          setDeleteAction(action) // Track if it's Trash (soft delete) or Delete (force delete)
        }
        setModalDescription(actionMessages[action])
        onOpen()
      }
    }

    const onValidate = async () => {
      try {
        // Extract base path from dataSource
        // Remove query parameters and common trash/archived paths
        let basePath = options.dataSource.split('?')[0]

        // Remove common trash/archived paths (can be at end or in middle)
        // Handle patterns like: /path/archived/resource or /path/resource/trashed
        basePath = basePath.replace(/\/archived\//g, '/') // Remove /archived/ from middle
        basePath = basePath.replace(/\/archived$/, '') // Remove /archived at end
        basePath = basePath.replace(/\/trashed$/, '') // Remove /trashed at end

        if (toDelete) {
          // Determine if it's a force delete (permanent) or soft delete
          const isForceDelete = deleteAction === 'Delete' || deleteAction === 'BulkDelete'
          const url = isForceDelete ? `${basePath}/${toDelete}/force` : `${basePath}/${toDelete}`
          await apiClient.delete(url)
          showSuccess('Success', isForceDelete ? 'Record permanently deleted successfully' : 'Record deleted successfully')
          setNextPage(null)
          getDataSource()
        }

        if (toRestore) {
          const url = `${basePath}/${toRestore}/restore`
          // All routes now use PATCH for restore (standardized)
          await apiClient.patch(url)
          showSuccess('Success', 'Record restored successfully')
          setNextPage(null)
          getDataSource()
        }
      } catch (error: any) {
        showError(error)
      } finally {
        onClose()
        setToDelete(null)
        setToRestore(null)
        setDeleteAction('')
      }
    }

    const getDataSource = async () => {
      setIsLoading(true)
      try {
        const dataSource = nextPage || options.dataSource
        const requestParams = {
          ...params,
          ...sortParams,
          ...filters,
        }

        const { data } = await apiClient.get(dataSource, { params: requestParams })

        if (data.data) {
          const meta = data.meta || {
            total: data.total ?? 0,
            per_page: data.per_page ?? data.data.length,
            current_page: data.current_page ?? 1,
            last_page: data.last_page ?? 1,
            from: data.from ?? null,
            to: data.to ?? null,
            links: data.links ?? [],
            path: data.path ?? '',
            first_page_url: data.first_page_url ?? null,
            last_page_url: data.last_page_url ?? null,
            next_page_url: data.next_page_url ?? null,
            prev_page_url: data.prev_page_url ?? null,
          }

          setTotalRows(meta.total ?? 0)
          if (options.softDelete) {
            setSubSub?.(meta.all || 0, meta.trashed || 0)
          }
          setDataRows(data.data)
          setMetaData(meta)
        } else {
          setTotalRows(data.length)
          setDataRows(data)
          setMetaData({
            total: data.length,
            per_page: data.length,
            current_page: 1,
            links: [],
          } as MetaData)
        }

        theadRef.current?.setCheckedAll(false)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        getDataSource()
      }, 300)

      return () => clearTimeout(timeoutId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, options.dataSource, sortParams, nextPage, filters])

    useEffect(() => {
      setNextPage(null)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, filters])

    useImperativeHandle(ref, () => ({
      getSelectedRows() {
        return tbodyRef.current?.getSelectedData() || []
      },
      clearPage() {
        setNextPage(null)
      },
      reload() {
        setDataRows([])
        getDataSource()
        theadRef.current?.setCheckedAll(false)
      },
      applyFilter(field: string, value: any) {
        handleFilter(field, value)
      },
    }))

    const dataColumnCount = Object.keys(options.dataFields).length
    const totalColumnCount = dataColumnCount + (options.selectable !== false ? 1 : 0) + 1 // data + checkbox (optional) + actions

    const renderTableContent = () => {
      if (isLoading) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={totalColumnCount} textAlign="center" py={8}>
                <VStack spacing={2}>
                  <Spinner size="lg" color={spinnerColor} />
                  <Text color={loadingTextColor}>Loading...</Text>
                </VStack>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      if (totalRows === 0 || dataRows.length === 0) {
        return (
          <Tbody>
            <Tr>
              <Td colSpan={totalColumnCount} textAlign="center" py={8}>
                <Text color={emptyTextColor}>{options.noRecordText || 'No Record Found'}</Text>
              </Td>
            </Tr>
          </Tbody>
        )
      }

      return (
        <TableBody
          options={options}
          rows={dataRows}
          ref={tbodyRef}
          permissions={access}
          onAction={handleAction}
          onCheckedAll={(checked) => theadRef.current?.setCheckedAll(checked)}
          customRenderers={customRenderers}
        />
      )
    }

    const renderPagination = () => {
      if (!isLoading && metaData && metaData.total > 0 && metaData.links && metaData.links.length > 0) {
        return <Pagination metas={metaData} onClick={setNextPage} />
      }
      return null
    }

    const pagination = renderPagination()

    return (
      <Box display="flex" flexDirection="column" h="100%" minH={0} position="relative" p={0} border="1px" borderColor={borderColor} borderRadius="md" bg={tableBg}>
        <Box
          flex="1"
          minH={0}
          overflowX="auto"
          overflowY="auto"
          position="relative"
          bg="transparent"
          css={{
            scrollbarColor: `${scrollbarTrack} transparent`,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: scrollbarTrack,
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: scrollbarThumbHover,
            },
            thead: {
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: theadBg,
            },
            'thead th': {
              backgroundColor: theadBg,
            },
          }}
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%">
            <TableHeader
              header={mergedDataFields}
              onCheckAll={(checked) => tbodyRef.current?.checkedAll(checked)}
              onSort={handleSort}
              onFilter={handleFilter}
              activeFilters={filters}
              ref={theadRef}
              showActionsColumn={true}
              showCheckbox={options.selectable !== false}
            />
            {renderTableContent()}
          </Table>
        </Box>

        {pagination ? (
          <Box flexShrink={0} px={6} pt={4} pb={4} borderTop="1px" borderColor={borderColor} bg={tableBg} position="relative" zIndex={5}>
            {pagination}
          </Box>
        ) : (
          <Box flexShrink={0} px={6} pt={4} pb={4} borderTop="1px" borderColor={borderColor} bg={tableBg} position="relative" zIndex={5} minH="60px" />
        )}

        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Confirmation</ModalHeader>
            <ModalBody>
              <Text>{modalDescription}</Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={onValidate}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    )
  }
)

DataTable.displayName = 'DataTable'


